import { useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { BookOpen, EllipsisVertical, Trash2 } from 'lucide-react';
import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  useToast,
} from '@/components/ui';
import { TypeNameConfirmDialog } from '@/components/ui/TypeNameConfirmDialog';
import { LivePageConnectionIndicator } from '@/components/ui/LivePageConnectionIndicator';
import { routes } from '@/lib/routes';
import {
  useAllKnowledgeBases,
  useDeleteKnowledgeBase,
} from '@/hooks/useKnowledgeBases';
import { useKbDeepLink } from '@/hooks/useKbDeepLink';
import { AuiFiles } from '@/components/aui/components/AuiFiles';
import type { KnowledgeBase } from '@/types/kb';

/**
 * Operator-side KB browser. Cross-space and read-only: this surface
 * shows every KB the user can see, but KB creation now happens at the
 * surface that owns the scope decision (console space page →
 * space-scoped, org admin → org-scoped).
 */
export default function KnowledgeBasesPage() {
  const toast = useToast();
  const { items, isLoading, error } = useAllKnowledgeBases();
  const remove = useDeleteKnowledgeBase();

  const params = useParams<{ kbId?: string; fileId?: string }>();
  const selectedKbId = params.kbId ?? null;

  const selectedKb = useMemo(
    () => items.find((kb) => kb.kbId === selectedKbId) ?? null,
    [items, selectedKbId],
  );

  // Two-way bind the selected file to the `/knowledge/:kbId/f/:fileId` URL.
  const deepLink = useKbDeepLink({
    kbId: selectedKbId,
    fileId: params.fileId,
    fileUrl: (fileId) => routes.operator.knowledgeBaseFile(selectedKbId ?? '', fileId),
    rootUrl: selectedKbId ? routes.operator.knowledgeBase(selectedKbId) : routes.operator.knowledge,
  });

  const [pendingDelete, setPendingDelete] = useState<KnowledgeBase | null>(null);

  // `Esc` focuses the file search inside the selected KB. The search input
  // lives deep in the shared AuiFiles surface, so we target it by its stable
  // aria-label within this page's container rather than threading a ref
  // through. Routed via react-hotkeys-hook so it shows up in the Shift+?
  // cheatsheet and inherits its built-in skip for input/textarea targets.
  const containerRef = useRef<HTMLDivElement>(null);
  useHotkeys(
    'escape',
    (event) => {
      const input = containerRef.current?.querySelector<HTMLInputElement>(
        'input[aria-label="Search files"]',
      );
      if (!input) return;
      event.preventDefault();
      input.focus();
      input.select();
    },
    {
      description: 'Focus file search',
      metadata: { group: 'Knowledge bases' },
    },
  );

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync({ kbId: pendingDelete.kbId });
      toast.success(`Deleted "${pendingDelete.name}"`);
      setPendingDelete(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <div ref={containerRef} className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Knowledge bases</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            File systems agents can read and search.
          </p>
        </div>
        <LivePageConnectionIndicator className="mt-2 mr-1 shrink-0" />
      </header>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {error.message}
        </div>
      )}

      <KbList
        kbs={items}
        isLoading={isLoading}
        selectedKbId={selectedKbId}
        onDelete={setPendingDelete}
      />

      {selectedKb && (
        // Fixed viewport-relative height so AuiFiles (`h-full` inside)
        // renders at a stable size. Selecting/swapping files no longer
        // resizes the page — each pane scrolls inside the container.
        <div className="h-[calc(100vh-22rem)] min-h-[420px]">
          <AuiFiles
            key={selectedKb.kbId}
            ownerId={selectedKb.kbId}
            title={selectedKb.name}
            previewPath={deepLink.previewPath}
            previewKey={deepLink.previewKey}
            onPreviewChange={deepLink.onPreviewChange}
            buildShareUrl={deepLink.buildShareUrl}
          />
        </div>
      )}

      <TypeNameConfirmDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete knowledge base?"
        message={
          pendingDelete
            ? `Deletes every file in "${pendingDelete.name}" and removes them from the search index. This cannot be undone.`
            : ''
        }
        expectedName={pendingDelete?.name ?? ''}
        isLoading={remove.isPending}
      />
    </div>
  );
}

function KbList({
  kbs,
  isLoading,
  selectedKbId,
  onDelete,
}: {
  kbs: KnowledgeBase[];
  isLoading: boolean;
  selectedKbId: string | null;
  onDelete: (kb: KnowledgeBase) => void;
}) {
  if (isLoading) {
    return (
      <div className="rounded-md border border-border bg-card p-6 text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (kbs.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        No knowledge bases yet. Create one in a space or organisation
        view to give agents access to files and semantic search.
      </div>
    );
  }

  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {kbs.map((kb) => {
        const isSelected = kb.kbId === selectedKbId;
        return (
          <li key={kb.kbId} className="group relative">
            <Link
              to={routes.operator.knowledgeBase(kb.kbId)}
              className={
                'flex h-full flex-col gap-2 rounded-md border bg-card p-4 transition-colors ' +
                (isSelected
                  ? 'border-primary-500 ring-1 ring-primary-500/30'
                  : 'border-border hover:bg-accent/20')
              }
            >
              <div className="flex items-start gap-3">
                <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-primary-500" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium text-foreground">
                      {kb.name}
                    </span>
                    {kb.scope === 'org' && <Badge variant="purple">org</Badge>}
                  </div>
                  {kb.spaceName && kb.scope === 'space' && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {kb.spaceName}
                    </p>
                  )}
                </div>
              </div>
              {kb.description && (
                <p className="line-clamp-2 text-xs text-muted-foreground">
                  {kb.description}
                </p>
              )}
            </Link>
            <div
              className="absolute right-2 top-2"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="KB actions"
                    className="rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
                  >
                    <EllipsisVertical className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={4}>
                  <DropdownMenuItem
                    onSelect={() => onDelete(kb)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
